import React, { useState, useEffect } from 'react';
import './QuestionManagement.css';
import { questionAPI } from '../services/api';
import { getApiUrl } from '../config/api';

const QuestionManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    questionId: '',
    question: '',
    icon: '',
    options: ['', '', '', ''],
    category: 'general',
    order: 1,
    isActive: true
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await questionAPI.getAllQuestions({ includeInactive: true });
      
      if (response.success) {
        setQuestions(response.data);
      } else {
        setError('Failed to load questions');
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load questions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    setFormData({ ...formData, options: [...formData.options, ''] });
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const resetForm = () => {
    setFormData({
      questionId: '',
      question: '',
      icon: '',
      options: ['', '', '', ''],
      category: 'general',
      order: questions.length + 1,
      isActive: true
    });
    setEditingQuestion(null);
    setShowForm(false);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      questionId: question.questionId,
      question: question.question,
      icon: question.icon,
      options: question.options,
      category: question.category,
      order: question.order,
      isActive: question.isActive
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        options: formData.options.filter(opt => opt.trim() !== ''),
        updatedBy: 'admin'
      };

      let response;
      if (editingQuestion) {
        // Update existing question
        response = await fetch(`${getApiUrl()}/questions/${editingQuestion._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        });
      } else {
        // Create new question
        response = await fetch(`${getApiUrl()}/questions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...submitData, createdBy: 'admin' })
        });
      }

      if (response.ok) {
        await fetchQuestions();
        resetForm();
        alert(editingQuestion ? 'Question updated successfully!' : 'Question created successfully!');
      } else {
        alert('Failed to save question');
      }
    } catch (err) {
      console.error('Error saving question:', err);
      alert('Error saving question. Please try again.');
    }
  };

  const handleDelete = async (questionId, questionTitle) => {
    if (window.confirm(`Are you sure you want to delete: "${questionTitle}"?`)) {
      try {
        const response = await fetch(`${getApiUrl()}/questions/${questionId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updatedBy: 'admin' })
        });

        if (response.ok) {
          await fetchQuestions();
          alert('Question deleted successfully!');
        } else {
          alert('Failed to delete question');
        }
      } catch (err) {
        console.error('Error deleting question:', err);
        alert('Error deleting question. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <div className="qm-loading">Loading questions...</div>;
  }

  if (error) {
    return (
      <div className="qm-error">
        <h3>Error Loading Questions</h3>
        <p>{error}</p>
        <button onClick={fetchQuestions} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="question-management">
      <div className="qm-header">
        <div>
          <h2>Question Management</h2>
          <p>Create, edit, or delete assessment questions</p>
        </div>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : '+ Add New Question'}
        </button>
      </div>

      {showForm && (
        <div className="qm-form-container">
          <h3>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h3>
          <form onSubmit={handleSubmit} className="qm-form">
            <div className="form-row">
              <div className="form-group">
                <label>Question ID *</label>
                <input
                  type="text"
                  value={formData.questionId}
                  onChange={(e) => handleInputChange('questionId', e.target.value)}
                  placeholder="e.g., q11"
                  required
                  disabled={editingQuestion !== null}
                />
              </div>
              <div className="form-group">
                <label>Icon (Emoji) *</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  placeholder="e.g., 📊"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Question Text *</label>
              <textarea
                value={formData.question}
                onChange={(e) => handleInputChange('question', e.target.value)}
                placeholder="Enter your question here..."
                rows="3"
                required
              />
            </div>

            <div className="form-group">
              <label>Options (minimum 2) *</label>
              {formData.options.map((option, index) => (
                <div key={index} className="option-input-group">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    required={index < 2}
                  />
                  {formData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="btn-remove-option"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addOption} className="btn-add-option">
                + Add Option
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="company-info">Company Info</option>
                  <option value="communication">Communication</option>
                  <option value="engagement">Engagement</option>
                  <option value="culture">Culture</option>
                  <option value="wellbeing">Wellbeing</option>
                  <option value="development">Development</option>
                </select>
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value))}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingQuestion ? 'Update Question' : 'Create Question'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="qm-list">
        <div className="qm-stats">
          <span>Total Questions: <strong>{questions.length}</strong></span>
          <span>Active: <strong>{questions.filter(q => q.isActive).length}</strong></span>
          <span>Inactive: <strong>{questions.filter(q => !q.isActive).length}</strong></span>
        </div>

        {questions.map((question) => (
          <div key={question._id} className={`question-card ${!question.isActive ? 'inactive' : ''}`}>
            <div className="question-card-header">
              <div className="question-title">
                <span className="question-icon-large">{question.icon}</span>
                <div>
                  <span className="question-id">{question.questionId}</span>
                  <h4>{question.question}</h4>
                </div>
              </div>
              <div className="question-actions">
                <button onClick={() => handleEdit(question)} className="btn-edit">
                  Edit
                </button>
                <button onClick={() => handleDelete(question._id, question.question)} className="btn-delete">
                  Delete
                </button>
              </div>
            </div>
            <div className="question-card-body">
              <div className="question-meta">
                <span className="badge">{question.category}</span>
                <span className="badge">Order: {question.order}</span>
                <span className={`badge ${question.isActive ? 'badge-success' : 'badge-danger'}`}>
                  {question.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="question-options">
                <strong>Options:</strong>
                <ol>
                  {question.options.map((option, index) => (
                    <li key={index}>{option}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionManagement;

